import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// This is like a list of settings we want to remember
interface Settings {
  // How the app looks
  theme: 'light' | 'dark' | 'system';
  // What language to use
  language: 'en' | 'es' | 'fr';
  // Should we show notifications?
  notifications: {
    email: boolean;
    push: boolean;
    sound: boolean;
  };
  // How to sort displays and sequences
  displaySort: 'newest' | 'popular' | 'price';
}

// This is our Settings toy box and what we can do with it
interface SettingsStore {
  // All our settings
  settings: Settings;
  
  // Change the theme (light/dark)
  setTheme: (theme: Settings['theme']) => void;
  
  // Change the language
  setLanguage: (language: Settings['language']) => void;
  
  // Update notification settings
  setNotifications: (notifications: Partial<Settings['notifications']>) => void;
  
  // Change how we sort things
  setDisplaySort: (sort: Settings['displaySort']) => void;
  
  // Reset all settings back to default
  resetSettings: () => void;
}

// Our default settings when we start fresh
const defaultSettings: Settings = {
  theme: 'dark',
  language: 'en',
  notifications: {
    email: true,
    push: true,
    sound: true,
  },
  displaySort: 'newest',
};

// Create our Settings toy box! ⚙️
export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set) => ({
      // Start with our default settings (force dark mode for all users)
      settings: defaultSettings,

      // Function to change theme
      setTheme: (theme) =>
        set((state) => ({
          settings: { ...state.settings, theme }
        })),

      // Function to change language
      setLanguage: (language) =>
        set((state) => ({
          settings: { ...state.settings, language }
        })),

      // Function to update notifications
      setNotifications: (notifications) =>
        set((state) => ({
          settings: {
            ...state.settings,
            notifications: {
              ...state.settings.notifications,
              ...notifications
            }
          }
        })),

      // Function to change sort order
      setDisplaySort: (displaySort) =>
        set((state) => ({
          settings: { ...state.settings, displaySort }
        })),

      // Function to reset everything back to default
      resetSettings: () =>
        set({ settings: defaultSettings }),
    }),
    {
      name: 'settings-storage', // Name for localStorage
      version: 1, // Increment this to force migration to dark mode
      migrate: (persistedState: any, version: number) => {
        // Force all users to start with dark mode (ignore previous preferences)
        if (version < 1) {
          return {
            settings: {
              ...defaultSettings,
              // Preserve non-theme settings if they exist
              language: persistedState?.settings?.language || defaultSettings.language,
              notifications: persistedState?.settings?.notifications || defaultSettings.notifications,
              displaySort: persistedState?.settings?.displaySort || defaultSettings.displaySort,
            }
          };
        }
        return persistedState;
      }
    }
  )
); 